import { CloseOutlined } from '@ant-design/icons';
import { landmineNames } from '@mm/constants/landmine-names';
import { useConnectedPageCRUDActions } from '@mm/state/actions/ConnectedPageCRUDActions';
import { useInvitesCRUDActions } from '@mm/state/actions/InvitesCRUDActions';
import { useUsersCRUDActions } from '@mm/state/actions/UsersCRUDActions';
import {
  BasicPage,
  PageBillingType,
} from '@mm/state/interfaces/connected-page';
import CompanyUsersModel, {
  isLiveChatAgentOrFacebookPageCollaborator,
  NewUserRole,
  UserRole,
} from '@mm/state/model/CompanyUsersModel';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { selectBasicPages } from '@mm/state/selectors/facebookPageSelector';
import { combinedFeatureFlagsSelector } from '@mm/state/selectors/featureFlagsSelectors';
import {
  activeCompanySelector,
  selectActiveCompanyId,
  selectCompanies,
} from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { Spin } from 'antd';
import { isEqual } from 'lodash';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { PrimaryButton } from '../../../common/buttons/PrimaryButton/PrimaryButton';
import { FacebookPagesAccess } from '../../../common/components/FacebookPagesAccess/FacebookPagesAccess';
import { ProfilePaneSection } from '../SettingsPage/MyAccountPanel/ProfilePaneSection/ProfilePaneSection';
import './UserEditor.pcss';

const disabledPageSelection: string[] = [
  UserRole.Owner,
  UserRole.Collaborator,
  UserRole.Admin,
];
const rolesWithBadgesMap: { [key: string]: boolean } = {
  [UserRole.Admin]: true,
  [UserRole.Collaborator]: true,
  [NewUserRole.Admin]: true,
  [NewUserRole.Collaborator]: true,
};
const rolesWithoutBadgesMap: { [key: string]: boolean } = {
  [UserRole.FacebookPageCollaborator]: true,
  [UserRole.LiveChatAgent]: true,
  [NewUserRole.FacebookPageCollaborator]: true,
  [NewUserRole.LiveChatAgent]: true,
  [NewUserRole.MonitorLeads]: true,
};

type Props = {
  companyId: number;
  userId: number;
};

export const UserEditor = ({ companyId, userId }: Props) => {
  const activeCompany = useSelector(activeCompanySelector);
  const pages = useSelector(selectBasicPages);
  const session = useSelector((state: StoreState) => state.session.session);
  const pagesWithAvailableSeats = useSelector(
    (state: StoreState) => state.invite.pagesWithAvailableSeats,
  );
  const companies = useSelector(selectCompanies);
  const activeCompanyId = useSelector(selectActiveCompanyId);
  const companyUsers = useSelector(
    (state: StoreState) => state.companyUsers.entities,
  );

  const editableUser = companyUsers.find(u => u.id === userId);

  const [user, setUser] = useState<CompanyUsersModel | undefined>(editableUser);
  const [isFetchUser, setIsFetchUser] = useState(false);
  const [pageSelectionDisabled, setPageSelectionDisabled] = useState(
    (user && !isLiveChatAgentOrFacebookPageCollaborator(user?.role)) || false,
  );
  const [selectedPages, setSelectedPages] = useState<BasicPage[]>(
    pages.filter(
      page =>
        pageSelectionDisabled ||
        !user?.allowedFacebookPageIds ||
        user?.allowedFacebookPageIds.indexOf(page.id) !== -1,
    ),
  );

  const {
    retrieveAll: retrieveUsers,
    updateById,
    updateActiveCompanyId,
  } = useUsersCRUDActions();
  const { retrieveAllByCompany: retrieveAllPages } =
    useConnectedPageCRUDActions();
  const { getAvailableSeats } = useInvitesCRUDActions();
  const { openUniversalUpgradeLandmineModal, closeModal } = useModalActions();

  const availableSeatsMap = pagesWithAvailableSeats.reduce((acc, curr) => {
    acc[curr] = true;
    return acc;
  }, {});

  const fbPages =
    pages.map(page => ({ ...page, disabled: !availableSeatsMap[page.id] })) ||
    [];

  const canEditUsers = !!useSelector(combinedFeatureFlagsSelector).userEdit;
  const canEditEmail = activeCompany?.editable || session.id === user?.id;

  const openUpgradeModal = () =>
    openUniversalUpgradeLandmineModal({
      landmineName: landmineNames['SETTINGS__COMPANY-PANEL'],
      planFallback: PageBillingType.TeamLabel,
    });

  const getEditable = () => {
    const activeCompany =
      companies && companies.find(c => c.id === activeCompanyId);
    const sameUser = session && user && session.id === user.id;
    const userIsOwner = user && user.role === UserRole.Owner;
    return activeCompany?.editable && !sameUser && !userIsOwner;
  };

  const updatePagesSelection = () => {
    if (user) {
      if (disabledPageSelection.indexOf(user?.role) !== -1) {
        setPageSelectionDisabled(true);
        setSelectedPages(pages);
      } else {
        setPageSelectionDisabled(false);
        setSelectedPages([]);
        setUser({ ...user, allowedFacebookPageIds: [] });
      }
    }
  };

  useEffect(() => {
    if (!isEqual(editableUser, user)) {
      updatePagesSelection();
    }
  }, [user?.role]);

  const onPagesSelected = (pages: BasicPage[]) => {
    const allowedFacebookPageIds = pages.map(page => page.id);

    const updatedUser = {
      ...user,
      allowedFacebookPageIds,
    } as CompanyUsersModel;

    if (!isEqual(updatedUser, user)) {
      setUser(updatedUser);
    }

    setSelectedPages([...pages]);
  };

  const onSave = async (canEditUser: boolean) => {
    if (
      !canEditUser &&
      user &&
      rolesWithBadgesMap[user.role] &&
      !rolesWithoutBadgesMap[user.role]
    ) {
      return openUpgradeModal();
    }

    const { id, role, allowedFacebookPageIds } = user || {};

    setIsFetchUser(true);
    if (id) {
      const success = await updateById(activeCompanyId, id, {
        role,
        email: user?.email,
        allowedFacebookPageIds,
      });
      setIsFetchUser(false);
      if (success) {
        setIsFetchUser(true);
        await retrieveUsers(Number(activeCompanyId));
        setIsFetchUser(false);
        setIsFetchUser(true);
        closeModal();
      }
    }
  };

  useEffect(() => {
    if (Number(companyId) !== -1) {
      setIsFetchUser(true);
      retrieveUsers(Number(companyId));
      retrieveAllPages(companyId);
      setIsFetchUser(false);
    }

    if (!pagesWithAvailableSeats || !pagesWithAvailableSeats.length) {
      getAvailableSeats(companyId);
    }

    if (session && activeCompanyId !== companyId && companies.length > 0) {
      const nextActiveCompany = companies.find(
        activeCompany => activeCompany.id === companyId,
      );

      if (nextActiveCompany) {
        updateActiveCompanyId(nextActiveCompany.id, session.id);
      }
    }
  }, []);

  if (user) {
    return (
      <div className="user-editor-component">
        <CloseOutlined
          className="user-editor-component__close-icon"
          onClick={() => closeModal()}
        />
        <div className="user-editor-component__header-content">Edit User</div>
        <ProfilePaneSection
          user={user}
          setUser={setUser}
          editableEmail={canEditEmail}
          editableRole={getEditable()}
          css={{ container: 'user-editor-component__profile-pane' }}
        />

        <div className="user-editor-component__facebook-pages">
          {activeCompany && activeCompany.editable && (
            <Spin size="small" spinning={isFetchUser}>
              <FacebookPagesAccess
                pages={fbPages}
                selectedPages={selectedPages}
                onPagesSelected={onPagesSelected}
                disabled={pageSelectionDisabled}
              />
            </Spin>
          )}
        </div>
        <div className="user-editor-component__header-buttons">
          <PrimaryButton
            className="user-editor-component__cancel-button"
            onClick={() => closeModal()}
            color="transparent-no-shadow"
          >
            Cancel
          </PrimaryButton>
          <PrimaryButton
            className="user-editor-component__button"
            onClick={() => onSave(canEditUsers)}
          >
            Save
          </PrimaryButton>
        </div>
      </div>
    );
  }

  return null;
};
